import styled from '@emotion/styled'
import React from 'react'
import type { UseFormRegister, UseFormWatch } from 'react-hook-form'
import { usePrevious } from 'react-use'

import ColorInput from './ColorInput'
import { EXAMPLE_COLOR_CODES } from './constants'
import ColorRadio from './radio/ColorRadio'
import FormRow from 'components/fields/FormRow'
import { FormValues } from 'components/forms/ThemeForm'
import type { ThemeVariant } from 'types/theme'

const ColorsRow = styled(FormRow)`
	max-width: 250px;
	flex-flow: row wrap;
	gap: ${({ theme }) => (theme.isMobile ? theme.spaces.space2 : theme.spaces.space4)};
	margin-bottom: ${({ theme }) => theme.spaces.space6};
`

const getOppositeVariantPrimaryColor = (color: string, currentVariant: ThemeVariant) => {
	const otherVariant = currentVariant === 'light' ? 'dark' : 'light'
	const eqivalentColorIndex = EXAMPLE_COLOR_CODES[otherVariant].findIndex(({ value }) => value === color)

	if (eqivalentColorIndex > -1) {
		return EXAMPLE_COLOR_CODES[currentVariant][eqivalentColorIndex].value
	}

	return color
}

type Props = {
	watch: UseFormWatch<FormValues>
	register: UseFormRegister<FormValues>
	onChange: (color: string) => void
}

const PrimaryColorForm: React.FC<Props> = ({ register, watch, onChange }) => {
	const watchPrimaryColor = watch('primaryColor')
	const watchVariant = watch('themeVariant')
	const previousVariant = usePrevious(watchVariant)
	const isCustomPrimaryColor = !EXAMPLE_COLOR_CODES[watchVariant].some(({ value }) => value === watchPrimaryColor)

	React.useEffect(() => {
		if (previousVariant !== watchVariant) {
			onChange(getOppositeVariantPrimaryColor(watchPrimaryColor, watchVariant))
		}
	}, [onChange, previousVariant, watchPrimaryColor, watchVariant])

	return (
		<ColorsRow>
			{EXAMPLE_COLOR_CODES[watchVariant].map(({ value, name }) => (
				<ColorRadio key={value} value={value} {...register('primaryColor')} aria-label={`color ${name}`} />
			))}
			<ColorInput value={isCustomPrimaryColor ? watchPrimaryColor : undefined} onChange={onChange} />
		</ColorsRow>
	)
}

export default PrimaryColorForm
